import type { ComponentProps } from 'react';
import type React from 'react';

import { Text } from '@change/ds-core-ui';
import { cn } from '@change/ds-utils';

import { TypedTranslate, useI18n } from '@change-corgi/core-react/i18n';

import { useBadgeScore } from './hooks/useBadgeScore';

type BadgeScoreProps = ComponentProps<'div'> & {
	score: number;
	smallText?: boolean;
	/**
	 * When set, the badge announces "Score: N out of 10" as a SINGLE screen-reader stop (petition-details
	 * strength carousel, where the card title "Petition strength" is the preceding stop and the rating the next).
	 * Off by default so surfaces with their own heading (e.g. the starter dashboard) keep the plain number.
	 */
	announceScoreLabel?: boolean;
};

export const BadgeScore: React.FC<BadgeScoreProps> = ({ score, smallText, announceScoreLabel, ...rest }) => {
	const { translate } = useI18n();
	const {
		data: { labelKey, color, backgroundColor },
	} = useBadgeScore(score);
	return (
		<div className="text-typography-strong flex items-center gap-4" {...rest}>
			<div
				data-qa="score-container"
				className={cn('grid min-w-[72px] place-items-center', announceScoreLabel && 'relative')}
			>
				<svg
					aria-hidden={announceScoreLabel || undefined}
					xmlns="http://www.w3.org/2000/svg"
					viewBox="0 0 16 16"
					fill={backgroundColor}
					className="col-[1/-1] row-[1/-1]"
				>
					<path d="M7.5.29a1 1 0 0 1 1 0l5.93 3.42c.3.18.5.51.5.87v6.84a1 1 0 0 1-.5.87L8.5 15.7a1 1 0 0 1-1 0L1.57 12.3a1 1 0 0 1-.5-.87V4.58a1 1 0 0 1 .5-.87L7.5.3Z" />
				</svg>
				<Text
					as="span"
					aria-hidden={announceScoreLabel || undefined}
					fontWeight="bold"
					className="col-[1/-1] row-[1/-1]"
					style={{ color, fontSize: 24, lineHeight: '40px' }}
				>
					{score}
				</Text>
				{announceScoreLabel && (
					/* The whole phrase is one text node (translate() output) so iOS VoiceOver reads it as a SINGLE stop —
					   interpolating the score as a separate JSX child splits it into its own stop. A transparent overlay
					   (not sr-only) keeps the VoiceOver focus rectangle on the visible hexagon rather than off-screen; VO
					   frames the wrapped text so the rectangle sits on the badge (a pixel-perfect box would need role="img",
					   which announces "image"). */
					<span className="absolute inset-0 overflow-hidden text-transparent">
						{translate('corgi.components.badge_score.score_label', { score })}
					</span>
				)}
			</div>
			<Text
				as="span"
				id="score-text"
				data-qa="score-text"
				size={smallText ? 'small' : undefined}
				className="min-w-0 flex-1 break-words"
			>
				<TypedTranslate value={labelKey} />
			</Text>
		</div>
	);
};
